import { useState, useCallback } from "react"
import Cookies from "js-cookie"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000"
const API = `${API_URL}/api/integrations`

function authHeader() {
    // The app stores the JWT in the `token` cookie (see AuthContext); other hooks
    // send it as `Bearer <token>`. Match that here.
    const token = Cookies.get("token")
    return token ? { Authorization: `Bearer ${token}` } : {}
}

export function useIntegrations() {
    const [providers, setProviders] = useState([])
    const [connections, setConnections] = useState([])
    const [userTier, setUserTier] = useState("free")
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const fetchProviders = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await fetch(API, { headers: authHeader() })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || "Failed to load providers")
            setProviders(data.providers || [])
            setUserTier(data.userTier || "free")
            // Flatten connections from all providers
            const allConns = (data.providers || []).flatMap(p => p.connections || [])
            setConnections(allConns)
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [])

    // Returns { authUrl } — caller opens this in a popup
    const initOAuth = useCallback(async (slug) => {
        const res = await fetch(`${API}/${slug}/connect/init`, {
            method: "POST",
            headers: authHeader(),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to start OAuth")
        return data.authUrl
    }, [])

    // Open OAuth popup and wait for postMessage result
    const connectOAuth = useCallback(async (slug) => {
        const authUrl = await initOAuth(slug)
        return new Promise((resolve, reject) => {
            const popup = window.open(authUrl, `oauth_${slug}`, "width=640,height=720,noopener=0")

            const handler = (e) => {
                if (e.data?.type === "integration_connected" && e.data.slug === slug) {
                    window.removeEventListener("message", handler)
                    clearInterval(closedTimer)
                    resolve(e.data.connection)
                }
                if (e.data?.type === "integration_error" && e.data.slug === slug) {
                    window.removeEventListener("message", handler)
                    clearInterval(closedTimer)
                    reject(new Error(e.data.error || "OAuth failed"))
                }
            }

            window.addEventListener("message", handler)

            // Detect popup closed by user
            const closedTimer = setInterval(() => {
                if (popup?.closed) {
                    clearInterval(closedTimer)
                    window.removeEventListener("message", handler)
                    reject(new Error("Popup closed before completing authorization"))
                }
            }, 800)
        })
    }, [initOAuth])

    // Connect API-key / webhook provider
    const connectApiKey = useCallback(async (slug, apiKey, metadata = {}) => {
        const res = await fetch(`${API}/${slug}/connect`, {
            method: "POST",
            headers: { ...authHeader(), "Content-Type": "application/json" },
            body: JSON.stringify({ api_key: apiKey, metadata }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to connect")
        return data
    }, [])

    const disconnect = useCallback(async (slug, connectionId) => {
        const res = await fetch(`${API}/${slug}/disconnect/${connectionId}`, {
            method: "DELETE",
            headers: authHeader(),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || `Failed to disconnect (HTTP ${res.status})`)
        return data
    }, [])

    const refreshConnection = useCallback(async (slug, connectionId) => {
        const res = await fetch(`${API}/${slug}/refresh/${connectionId}`, {
            method: "POST",
            headers: authHeader(),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || `Failed to refresh (HTTP ${res.status})`)
        return data
    }, [])

    const getConnectionDetail = useCallback(async (connectionId) => {
        const res = await fetch(`${API}/connections/${connectionId}`, { headers: authHeader() })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to load connection")
        return data  // { connection, logs }
    }, [])

    // Form-level integrations
    const listFormIntegrations = useCallback(async (formId) => {
        const res = await fetch(`${API_URL}/api/forms/${formId}/integrations`, { headers: authHeader() })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to load form integrations")
        return data.integrations || []
    }, [])

    const createFormIntegration = useCallback(async (formId, connectionId, action, config = {}) => {
        const res = await fetch(`${API_URL}/api/forms/${formId}/integrations`, {
            method: "POST",
            headers: { ...authHeader(), "Content-Type": "application/json" },
            body: JSON.stringify({ connectionId, action, config }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to create integration")
        return data
    }, [])

    const deleteFormIntegration = useCallback(async (formId, integrationId) => {
        const res = await fetch(`${API_URL}/api/forms/${formId}/integrations/${integrationId}`, {
            method: "DELETE",
            headers: authHeader(),
        })
        if (!res.ok) throw new Error("Failed to delete integration")
    }, [])

    const listActions = useCallback(async () => {
        const res = await fetch(`${API}/actions`, { headers: authHeader() })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to load actions")
        return data.connections || []
    }, [])

    const testAction = useCallback(async (connectionId, action, payload) => {
        const res = await fetch(`${API}/connections/${connectionId}/test`, {
            method: "POST",
            headers: { ...authHeader(), "Content-Type": "application/json" },
            body: JSON.stringify({ action, payload }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Test failed")
        return data  // { success, output, latencyMs }
    }, [])

    return {
        providers,
        connections,
        userTier,
        loading,
        error,
        fetchProviders,
        connectOAuth,
        connectApiKey,
        disconnect,
        refreshConnection,
        getConnectionDetail,
        listFormIntegrations,
        createFormIntegration,
        deleteFormIntegration,
        listActions,
        testAction,
    }
}
